import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Alert, CircularProgress,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel, IconButton, Divider, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import UploadIcon from '@mui/icons-material/Upload';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';

const API = process.env.REACT_APP_API_URL || '';

const COLORES = [
  { clave: 'color_fondo',      label: 'Fondo principal',        desc: 'Color de fondo de toda la app' },
  { clave: 'color_superficie', label: 'Superficies y paneles',  desc: 'Sidebar, header, tarjetas, dialogs' },
  { clave: 'color_primario',   label: 'Color primario',         desc: 'Botones, ítem activo del menú' },
  { clave: 'color_acento',     label: 'Color de acento',        desc: 'Íconos, labels, textos destacados' },
];

const DEFECTO = {
  color_fondo:      '#1a1a2e',
  color_superficie: '#16213e',
  color_primario:   '#0f3460',
  color_acento:     '#90caf9',
};

const TIPO_OPCIONES = [
  { value: 'info',    label: 'Información', color: '#1565c0' },
  { value: 'warning', label: 'Aviso',       color: '#e65100' },
  { value: 'success', label: 'Noticia',     color: '#2e7d32' },
  { value: 'error',   label: 'Urgente',     color: '#c62828' },
];

export default function Configuracion({ onTemaActualizado }) {
  const [tema, setTema] = useState(DEFECTO);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [exito, setExito] = useState(false);
  const [error, setError] = useState('');

  const [avisos, setAvisos] = useState([]);
  const [nuevoAviso, setNuevoAviso] = useState({ texto: '', tipo: 'info' });
  const [guardandoAviso, setGuardandoAviso] = useState(false);
  const [exitoAviso, setExitoAviso] = useState('');

  // Reset datos de prueba
  const [dialogReset, setDialogReset] = useState(false);
  const [confirmTexto, setConfirmTexto] = useState('');
  const [reseteando, setReseteando] = useState(false);
  const [resultadoReset, setResultadoReset] = useState('');

  // Pantalla de inicio
  const [inicio, setInicio] = useState({ nombre_clinica: '', subtitulo: '', logo_base64: '' });
  const [guardandoInicio, setGuardandoInicio] = useState(false);
  const [exitoInicio, setExitoInicio] = useState('');
  const [errorInicio, setErrorInicio] = useState('');
  const logoInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/configuracion/tema`)
      .then(r => setTema({ ...DEFECTO, ...r.data }))
      .catch(() => {})
      .finally(() => setCargando(false));
    cargarAvisos();
    axios.get(`${API}/api/configuracion/inicio`)
      .then(r => setInicio(r.data))
      .catch(() => {});
  }, []);

  function cargarAvisos() {
    axios.get(`${API}/api/avisos/todos`)
      .then(r => setAvisos(r.data))
      .catch(() => {});
  }

  async function crearAviso() {
    if (!nuevoAviso.texto.trim()) return;
    setGuardandoAviso(true);
    try {
      await axios.post(`${API}/api/avisos`, nuevoAviso);
      setNuevoAviso({ texto: '', tipo: 'info' });
      cargarAvisos();
      setExitoAviso('Aviso creado.');
      setTimeout(() => setExitoAviso(''), 3000);
    } catch (e) {
      setExitoAviso('Error: ' + (e.response?.data?.error || e.message));
    } finally {
      setGuardandoAviso(false);
    }
  }

  async function toggleAviso(av) {
    await axios.put(`${API}/api/avisos/${av.id}`, { ...av, activo: av.activo ? 0 : 1 });
    cargarAvisos();
  }

  async function eliminarAviso(id) {
    await axios.delete(`${API}/api/avisos/${id}`);
    cargarAvisos();
  }

  async function guardar() {
    setGuardando(true);
    setExito(false);
    setError('');
    try {
      await axios.put(`${API}/api/configuracion/tema`, tema);
      onTemaActualizado(tema);
      setExito(true);
      setTimeout(() => setExito(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar.');
    } finally {
      setGuardando(false);
    }
  }

  function resetear() {
    setTema(DEFECTO);
  }

  async function handleResetear() {
    setReseteando(true);
    setResultadoReset('');
    try {
      await axios.delete(`${API}/api/admin/resetear-datos`);
      setResultadoReset('ok');
    } catch (e) {
      setResultadoReset('error: ' + (e.response?.data?.error || e.message));
    } finally {
      setReseteando(false);
      setDialogReset(false);
      setConfirmTexto('');
    }
  }

  function handleLogoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setErrorInicio('El logo no puede superar 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = ev => setInicio(i => ({ ...i, logo_base64: ev.target.result }));
    reader.readAsDataURL(file);
  }

  async function guardarInicio() {
    setGuardandoInicio(true);
    setErrorInicio('');
    setExitoInicio('');
    try {
      await axios.put(`${API}/api/configuracion/inicio`, inicio);
      setExitoInicio('Pantalla de inicio guardada.');
      setTimeout(() => setExitoInicio(''), 3000);
    } catch (e) {
      setErrorInicio(e.response?.data?.error || 'Error al guardar.');
    } finally {
      setGuardandoInicio(false);
    }
  }

  if (cargando) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress sx={{ color: 'var(--c-acento)' }} />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Configuración de colores
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 4, fontSize: 14 }}>
        Los cambios se aplican en tiempo real al guardar.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {COLORES.map(({ clave, label, desc }) => (
          <Box key={clave} sx={{
            display: 'flex', alignItems: 'center', gap: 3,
            bgcolor: 'var(--c-superficie)', p: 2, borderRadius: 2,
            border: '1px solid var(--c-primario)'
          }}>
            {/* Preview del color actual */}
            <Box sx={{
              width: 48, height: 48, borderRadius: 2, flexShrink: 0,
              bgcolor: tema[clave], border: '2px solid rgba(255,255,255,0.15)',
              position: 'relative', overflow: 'hidden', cursor: 'pointer'
            }}>
              <input
                type="color"
                value={tema[clave]}
                onChange={e => setTema(t => ({ ...t, [clave]: e.target.value }))}
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  opacity: 0, cursor: 'pointer', border: 'none', padding: 0
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: 15 }}>
                {label}
              </Typography>
              <Typography sx={{ color: '#90a4ae', fontSize: 12 }}>{desc}</Typography>
              <Typography sx={{ color: 'var(--c-acento)', fontSize: 12, fontFamily: 'monospace', mt: 0.5 }}>
                {tema[clave]}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Preview del tema */}
      <Box sx={{ mt: 4, p: 2, borderRadius: 2, bgcolor: tema.color_fondo, border: '1px solid rgba(255,255,255,0.1)' }}>
        <Typography sx={{ color: '#b0b0b0', fontSize: 12, mb: 1.5 }}>Vista previa</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ bgcolor: tema.color_superficie, px: 2, py: 1, borderRadius: 1 }}>
            <Typography sx={{ color: tema.color_acento, fontSize: 13 }}>Panel</Typography>
          </Box>
          <Box sx={{ bgcolor: tema.color_primario, px: 2, py: 1, borderRadius: 1 }}>
            <Typography sx={{ color: '#fff', fontSize: 13 }}>Botón</Typography>
          </Box>
          <Box sx={{ bgcolor: tema.color_superficie, px: 2, py: 1, borderRadius: 1, border: `1px solid ${tema.color_primario}` }}>
            <Typography sx={{ color: tema.color_acento, fontSize: 13 }}>⚙ Menú activo</Typography>
          </Box>
        </Box>
      </Box>

      {exito && (
        <Alert severity="success" sx={{ mt: 3 }}>
          Colores guardados correctamente.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={resetear}
          sx={{ color: '#b0b0b0', borderColor: '#b0b0b0' }}>
          Restaurar defaults
        </Button>
        <Button variant="contained" onClick={guardar} disabled={guardando}
          sx={{ bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
          {guardando ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </Box>

      {/* ── Avisos / Banner ─────────────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Banner de avisos
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
        Los avisos activos se muestran como ticker desplazante debajo del encabezado para todos los usuarios.
      </Typography>

      {/* Nuevo aviso */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <TextField
          label="Texto del aviso"
          value={nuevoAviso.texto}
          onChange={e => setNuevoAviso(a => ({ ...a, texto: e.target.value }))}
          onKeyDown={e => e.key === 'Enter' && crearAviso()}
          sx={{ flex: 1, minWidth: 220,
            '& .MuiInputBase-root': { color: '#e0e0e0' },
            '& .MuiInputLabel-root': { color: '#90a4ae' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' }
          }}
        />
        <FormControl sx={{ minWidth: 140 }}>
          <InputLabel sx={{ color: '#90a4ae' }}>Tipo</InputLabel>
          <Select
            value={nuevoAviso.tipo}
            label="Tipo"
            onChange={e => setNuevoAviso(a => ({ ...a, tipo: e.target.value }))}
            sx={{ color: '#e0e0e0',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' },
              '& .MuiSvgIcon-root': { color: '#e0e0e0' }
            }}
          >
            {TIPO_OPCIONES.map(t => (
              <MenuItem key={t.value} value={t.value}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color }} />
                  {t.label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={crearAviso}
          disabled={guardandoAviso || !nuevoAviso.texto.trim()}
          sx={{ bgcolor: 'var(--c-primario)', height: 56, '&:hover': { filter: 'brightness(1.2)' } }}
        >
          Agregar
        </Button>
      </Box>

      {exitoAviso && (
        <Alert severity={exitoAviso.startsWith('Error') ? 'error' : 'success'} sx={{ mb: 2 }}>
          {exitoAviso}
        </Alert>
      )}

      {/* Lista de avisos */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {avisos.length === 0 && (
          <Typography sx={{ color: '#666', fontSize: 14 }}>No hay avisos creados.</Typography>
        )}
        {avisos.map(av => {
          const tipo = TIPO_OPCIONES.find(t => t.value === av.tipo) || TIPO_OPCIONES[0];
          return (
            <Box key={av.id} sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              bgcolor: 'var(--c-superficie)', p: 1.5, borderRadius: 2,
              border: `1px solid ${av.activo ? tipo.color : 'rgba(255,255,255,0.08)'}`,
              opacity: av.activo ? 1 : 0.5
            }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tipo.color, flexShrink: 0 }} />
              <Typography sx={{ flex: 1, color: '#e0e0e0', fontSize: 14, wordBreak: 'break-word' }}>
                {av.texto}
              </Typography>
              <Chip label={tipo.label} size="small" sx={{ bgcolor: tipo.color + '33', color: tipo.color, fontSize: 11 }} />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!av.activo}
                    onChange={() => toggleAviso(av)}
                    size="small"
                    sx={{ '& .MuiSwitch-thumb': { bgcolor: av.activo ? tipo.color : '#555' } }}
                  />
                }
                label={<Typography sx={{ fontSize: 12, color: '#90a4ae' }}>{av.activo ? 'Activo' : 'Inactivo'}</Typography>}
                sx={{ m: 0 }}
              />
              <IconButton onClick={() => eliminarAviso(av.id)} size="small" sx={{ color: '#ef9a9a' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
      </Box>

      {/* ── Pantalla de inicio ───────────────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--c-acento)', mb: 1 }}>
        Pantalla de inicio
      </Typography>
      <Typography sx={{ color: '#b0b0b0', mb: 3, fontSize: 14 }}>
        Personalizá el logo, el nombre y el subtítulo que ven los usuarios en la pantalla de login.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

        {/* Preview del login */}
        <Box sx={{
          bgcolor: 'var(--c-fondo)', border: '1px solid var(--c-primario)',
          borderRadius: 2, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1
        }}>
          <Typography sx={{ color: '#90a4ae', fontSize: 11, mb: 1, alignSelf: 'flex-start' }}>Vista previa del login</Typography>
          {inicio.logo_base64
            ? <img src={inicio.logo_base64} alt="logo" style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8 }} />
            : <LocalHospitalIcon sx={{ fontSize: 52, color: 'var(--c-acento)' }} />
          }
          <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: 18 }}>
            {inicio.nombre_clinica || 'Sistema Médico'}
          </Typography>
          <Typography sx={{ color: '#b0b0b0', fontSize: 13 }}>
            {inicio.subtitulo || 'Gestión de turnos e historiales'}
          </Typography>
        </Box>

        {/* Logo */}
        <Box sx={{ bgcolor: 'var(--c-superficie)', p: 2, borderRadius: 2, border: '1px solid var(--c-primario)' }}>
          <Typography sx={{ color: '#e0e0e0', fontWeight: 600, mb: 1 }}>Logo</Typography>
          <Typography sx={{ color: '#90a4ae', fontSize: 12, mb: 1.5 }}>
            PNG o JPG, máximo 2 MB. Se mostrará en la pantalla de ingreso.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {inicio.logo_base64 && (
              <img src={inicio.logo_base64} alt="logo actual" style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)' }} />
            )}
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => logoInputRef.current?.click()}
              sx={{ color: 'var(--c-acento)', borderColor: 'var(--c-primario)', '&:hover': { borderColor: 'var(--c-acento)' } }}
            >
              {inicio.logo_base64 ? 'Cambiar logo' : 'Subir logo'}
            </Button>
            {inicio.logo_base64 && (
              <Button variant="text" size="small" onClick={() => setInicio(i => ({ ...i, logo_base64: '' }))}
                sx={{ color: '#ef9a9a', fontSize: 12, textTransform: 'none' }}>
                Quitar logo
              </Button>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={handleLogoChange}
            />
          </Box>
        </Box>

        {/* Nombre y subtítulo */}
        <TextField
          label="Nombre de la clínica / consultorio"
          value={inicio.nombre_clinica}
          onChange={e => setInicio(i => ({ ...i, nombre_clinica: e.target.value }))}
          placeholder="Sistema Médico"
          inputProps={{ maxLength: 80, style: { color: '#e0e0e0' } }}
          InputLabelProps={{ style: { color: '#90a4ae' } }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
        />
        <TextField
          label="Subtítulo"
          value={inicio.subtitulo}
          onChange={e => setInicio(i => ({ ...i, subtitulo: e.target.value }))}
          placeholder="Gestión de turnos e historiales"
          inputProps={{ maxLength: 120, style: { color: '#e0e0e0' } }}
          InputLabelProps={{ style: { color: '#90a4ae' } }}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--c-primario)' } }}
        />
      </Box>

      {exitoInicio && <Alert severity="success" sx={{ mt: 2 }}>{exitoInicio}</Alert>}
      {errorInicio && <Alert severity="error" sx={{ mt: 2 }}>{errorInicio}</Alert>}

      <Button variant="contained" onClick={guardarInicio} disabled={guardandoInicio}
        sx={{ mt: 3, bgcolor: 'var(--c-primario)', '&:hover': { filter: 'brightness(1.2)' } }}>
        {guardandoInicio ? 'Guardando...' : 'Guardar pantalla de inicio'}
      </Button>

      {/* ── Zona de peligro ─────────────────────────────────────────── */}
      <Divider sx={{ my: 5, borderColor: 'rgba(255,255,255,0.1)' }} />

      <Box sx={{
        border: '1px solid #c62828', borderRadius: 2, p: 3,
        bgcolor: 'rgba(198,40,40,0.07)'
      }}>
        <Typography variant="h6" sx={{ color: '#ef9a9a', fontWeight: 700, mb: 0.5 }}>
          Zona de pruebas
        </Typography>
        <Typography sx={{ color: '#b0b0b0', fontSize: 13, mb: 2 }}>
          ⚠️ <strong style={{ color: '#ef9a9a' }}>Borrar este botón al finalizar pruebas.</strong>{' '}
          Elimina todos los pacientes, médicos, turnos e historias clínicas cargados durante las pruebas. Los usuarios admin no se eliminan.
        </Typography>

        {resultadoReset === 'ok' && (
          <Alert severity="success" sx={{ mb: 2 }}>Datos de prueba eliminados correctamente.</Alert>
        )}
        {resultadoReset.startsWith('error') && (
          <Alert severity="error" sx={{ mb: 2 }}>{resultadoReset}</Alert>
        )}

        <Button
          variant="outlined"
          onClick={() => { setDialogReset(true); setConfirmTexto(''); setResultadoReset(''); }}
          sx={{ color: '#ef9a9a', borderColor: '#c62828', '&:hover': { bgcolor: 'rgba(198,40,40,0.15)' } }}
        >
          Resetear datos de prueba
        </Button>
      </Box>

      {/* Dialog de confirmación */}
      <Dialog open={dialogReset} onClose={() => setDialogReset(false)}
        PaperProps={{ sx: { bgcolor: 'var(--c-superficie)', border: '1px solid #c62828', borderRadius: 2 } }}>
        <DialogTitle sx={{ color: '#ef9a9a', fontWeight: 700 }}>⚠️ Confirmar reseteo</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#e0e0e0', mb: 2, fontSize: 14 }}>
            Esta acción va a eliminar <strong>permanentemente</strong>:
          </Typography>
          <Box component="ul" sx={{ color: '#b0b0b0', fontSize: 13, pl: 2, mb: 2 }}>
            <li>Todos los pacientes</li>
            <li>Todos los médicos</li>
            <li>Todos los turnos</li>
            <li>Todas las historias clínicas y evoluciones</li>
            <li>Todo el staff (excepto administradores)</li>
          </Box>
          <Typography sx={{ color: '#e0e0e0', fontSize: 14, mb: 1.5 }}>
            Escribí <strong style={{ color: '#ef9a9a' }}>CONFIRMAR</strong> para continuar:
          </Typography>
          <TextField
            fullWidth
            value={confirmTexto}
            onChange={e => setConfirmTexto(e.target.value)}
            placeholder="CONFIRMAR"
            inputProps={{ style: { color: '#e0e0e0', letterSpacing: 2 } }}
            sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c62828' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogReset(false)} sx={{ color: '#b0b0b0' }}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={confirmTexto !== 'CONFIRMAR' || reseteando}
            onClick={handleResetear}
            sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}
          >
            {reseteando ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Borrar todo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
